import BackwardOutlined from '@ant-design/icons/BackwardOutlined'
import CaretRightOutlined from '@ant-design/icons/CaretRightOutlined'
import ForwardOutlined from '@ant-design/icons/ForwardOutlined'
import PauseCircleFilled from '@ant-design/icons/PauseCircleFilled'
import Select from 'antd/lib/select'
import Slider from 'antd/lib/slider'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './AudioViewer.module.less'
import { formatTimeWithMilliseconds } from 'Utils/common.utils'
import { useMessage } from 'shared/hooks/use-message'
import { CopyIcon } from '../Icons/CopyIcon'

type AudioViewerProps = {
    url: string
}

const PLAYBACK_RATE_OPTIONS = [
    { value: 0.5, label: '0.5x' },
    { value: 1, label: '1x' },
    { value: 1.5, label: '1.5x' },
    { value: 2, label: '2x' },
]

export const AudioViewer: React.FC<AudioViewerProps> = ({ url }) => {
    const audioRef = useRef<HTMLAudioElement>(null)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [progress, setProgress] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [duration, setDuration] = useState<string>('00:00.000')
    const [currentTime, setCurrentTime] = useState<string>('00:00.000')
    const [messageApi] = useMessage()

    const handleCopyCurrentTimeButtonClick = () => {
        if (!audioRef.current) return
        const currentTime = audioRef.current.currentTime ?? 0
        navigator.clipboard
            .writeText(formatTimeWithMilliseconds(currentTime))
            .then(() => {
                messageApi.success('Timestamp Copied')
            })
            .catch(() => {
                messageApi.error('Failed to Copy Timestamp')
            })
    }

    useEffect(() => {
        const audio = audioRef.current

        if (!audio) return

        const setAudioData = () => {
            setDuration(formatTimeWithMilliseconds(audio.duration))
        }

        const handleTimeUpdate = () => {
            const currentProgress = (audio.currentTime / audio.duration) * 100

            setProgress(currentProgress)
            setCurrentTime(formatTimeWithMilliseconds(audio.currentTime))
        }

        const handleEnded = () => {
            setIsPlaying(false)
            setProgress(0)
            audio.currentTime = 0
        }

        audio.addEventListener('loadeddata', setAudioData)
        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('ended', handleEnded)

        return () => {
            audio.removeEventListener('loadeddata', setAudioData)
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('ended', handleEnded)
        }
    }, [])

    const togglePlay = useCallback(() => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause()
            } else {
                audioRef.current.play()
            }
            setIsPlaying(!isPlaying)
        }
    }, [isPlaying])

    const handlePlaybackRateChange = useCallback((value: number) => {
        setPlaybackRate(value)
        if (audioRef.current) {
            audioRef.current.playbackRate = value
        }
    }, [])

    const handleProgressChange = useCallback((value: number) => {
        if (audioRef.current) {
            const time = (value / 100) * audioRef.current.duration

            audioRef.current.currentTime = time
            setProgress(value)
        }
    }, [])

    const skipForward = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.min(
                audioRef.current.currentTime + 10,
                audioRef.current.duration
            )
        }
    }, [])

    const skipBackward = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 10, 0)
        }
    }, [])

    return (
        <div className={styles['audio-container']}>
            <div className={styles['audio-controls']}>
                <button
                    data-testid="backward-button"
                    className={`${styles['control-button']} ${styles['skip-back']}`}
                    onClick={skipBackward}
                    title="Skip backward 10 seconds"
                >
                    <BackwardOutlined />
                </button>
                <button
                    data-testid="play-pause-button"
                    className={`${styles['control-button']} ${styles['play-pause']}`}
                    onClick={togglePlay}
                    title={isPlaying ? 'Pause' : 'Play'}
                >
                    {isPlaying ? <PauseCircleFilled /> : <CaretRightOutlined />}
                </button>
                <button
                    data-testid="forward-button"
                    className={`${styles['control-button']} ${styles['skip-forward']}`}
                    onClick={skipForward}
                    title="Skip forward 10 seconds"
                >
                    <ForwardOutlined />
                </button>
                <button
                    data-testid="copy-button"
                    className={`${styles['control-button']}`}
                    onClick={handleCopyCurrentTimeButtonClick}
                    title="Copy timestamp"
                >
                    <CopyIcon />
                </button>
                <div className={styles['time-display']}>
                    <span className={styles['current-time']}>{currentTime}</span>
                    <span> / </span>
                    <span>{duration}</span>
                </div>
                <Slider
                    className={styles['progress-slider']}
                    value={progress}
                    onChange={handleProgressChange}
                    tooltip={{ formatter: null }}
                />
                <Select
                    popupMatchSelectWidth={false}
                    className={styles['playback-rate-select']}
                    value={playbackRate}
                    onChange={handlePlaybackRateChange}
                    options={PLAYBACK_RATE_OPTIONS}
                />
            </div>
            <audio
                ref={audioRef}
                style={{ display: 'none' }}
                controlsList="nodownload"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
            >
                <source src={url} />
                Your browser does not support the audio element.
            </audio>
        </div>
    )
}
